import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { trimPhoneNumber } from 'src/app/common-components/helpers/trim-phone-number-helper';
import { phoneNumberValidator } from 'src/app/common-components/validators/phone-number.validator';
import { PhoneNumberDetailForm, PhoneNumberDetailFormValue } from '../models/phone-number-detail-form';
import { PhoneNumberDetailSaveModel } from '../models/phone-number-detail-save.model';
import { PhoneNumberDetailView } from '../models/phone-number-detail-view.model';
import * as PhoneNumberDetailActions from '../state/phone-number-detail.actions';

@Injectable()
export class PhoneNumberDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(fb: UntypedFormBuilder, private store: Store, private action: Actions) {
      super();

      const form: PhoneNumberDetailForm = {
         account: fb.control(null),
         allowOutboundCalls: fb.control(null),
         closedExtension: fb.control(null),
         createDate: fb.control(null),
         createUser: fb.control(null),
         datePorted: fb.control(null),
         displayName: fb.control(null),
         faxExtension: fb.control(null),
         isActive: fb.control(null),
         isPorted: fb.control(null),
         isFax: fb.control(null),
         location: fb.control(null),
         notes: fb.control(null),
         openExtension: fb.control(null),
         routeTo: fb.control(null),
         phoneNumber: fb.control(null, phoneNumberValidator()),
         providerId: fb.control(null),
         schedule: fb.control(null),
         timeZone: fb.control(null),
         updateDate: fb.control(null),
         updateUser: fb.control(null),
      };

      this.form = fb.group(form);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updatePhoneNumber(phoneNumber: PhoneNumberDetailView): void {
      if (!phoneNumber) {
         return;
      }

      const formValue: Partial<PhoneNumberDetailFormValue> = {
         account: phoneNumber.account,
         allowOutboundCalls: phoneNumber.allowOutboundCalls,
         closedExtension: phoneNumber.closedExtension,
         datePorted: phoneNumber.datePorted,
         displayName: phoneNumber.displayName,
         faxExtension: phoneNumber.faxExtension,
         isActive: phoneNumber.isActive,
         isPorted: phoneNumber.isPorted,
         isFax: phoneNumber.faxExtension !== null,
         location: phoneNumber.location,
         notes: phoneNumber.notes,
         openExtension: phoneNumber.openExtension,
         phoneNumber: phoneNumber.phoneNumber,
         providerId: phoneNumber.providerId,
         schedule: phoneNumber.schedule,
         timeZone: { timeZoneId: phoneNumber?.timeZoneId ?? null },
      };

      this.form.patchValue(formValue);

      const openExtensionControl = this.form.get('openExtension');
      const closedExtensionControl = this.form.get('closedExtension');

      if (phoneNumber.account && phoneNumber.account.accountId) {
         this.store.dispatch(
            PhoneNumberDetailActions.verifyRoute({
               accountId: phoneNumber.account.accountId,
               phoneNumber: phoneNumber.phoneNumberId,
            })
         );
         if (phoneNumber.isActive) {
            openExtensionControl?.setValidators(Validators.required);
            closedExtensionControl?.setValidators(Validators.required);
         }
      } else {
         this.store.dispatch(
            PhoneNumberDetailActions.verifyRoute({
               accountId: phoneNumber.account?.accountId ?? null,
               phoneNumber: phoneNumber.phoneNumberId,
            })
         );
         openExtensionControl?.setValidators(null);
         closedExtensionControl?.setValidators(null);
      }
      openExtensionControl?.updateValueAndValidity({ emitEvent: false });
      closedExtensionControl?.updateValueAndValidity({ emitEvent: false });

      this.markAsUntouchedAndPristine();
   }

   getValue(phoneNumberId: number | null): PhoneNumberDetailSaveModel {
      const value = this.form.value as PhoneNumberDetailFormValue;
      return {
         accountId: value.account?.accountId ?? null,
         allowOutboundCalls: value.allowOutboundCalls,
         closedExtensionId: value.closedExtension?.extensionId ?? null,
         datePorted: value.datePorted,
         displayName: value.displayName,
         faxExtensionId: value.faxExtension?.extensionId ?? null,
         isActive: value.isActive,
         isPorted: value.isPorted,
         locationId: value.location?.locationId ?? null,
         notes: value.notes,
         openExtensionId: value.openExtension?.extensionId ?? null,
         phoneNumber: trimPhoneNumber(value.phoneNumber),
         phoneNumberId: phoneNumberId,
         providerId: value.providerId,
         scheduleId: value.schedule?.scheduleId ?? null,
         timeZoneId: value.timeZone?.timeZoneId ?? null,
      };
   }

   toggleSystemFields(enabled: boolean, canEditPhoneNumberSettings: boolean): void {
      const allowOutboundCallsControl = this.form.get('allowOutboundCalls');
      const locationControl = this.form.get('location');

      if (enabled && canEditPhoneNumberSettings) {
         allowOutboundCallsControl?.enable();
         locationControl?.enable();
      } else {
         allowOutboundCallsControl?.disable();
         locationControl?.disable();
      }
   }
}
